// ================================
// NavContentComponent (REFactored)
// ================================

// Angular imports
import { Component, OnInit, inject, output } from '@angular/core';
import { CommonModule, Location, LocationStrategy } from '@angular/common';
import { RouterModule } from '@angular/router';

// Project imports
import { NavigationItem, NavigationItems } from '../navigation';
import { environment } from 'src/environments/environment';
import { NavGroupComponent } from './nav-group/nav-group.component';

// Icons
import { IconService } from '@ant-design/icons-angular';
import {
  DashboardOutline,
  CreditCardOutline,
  LoginOutline,
  QuestionOutline,
  ChromeOutline,
  FontSizeOutline,
  ProfileOutline,
  BgColorsOutline,
  AntDesignOutline
} from '@ant-design/icons-angular/icons';

import { NgScrollbarModule } from 'ngx-scrollbar';

@Component({
  selector: 'app-nav-content',
  standalone: true,
  imports: [CommonModule, RouterModule, NavGroupComponent, NgScrollbarModule],
  templateUrl: './nav-content.component.html',
  styleUrls: ['./nav-content.component.scss']
})
export class NavContentComponent implements OnInit {

  // -----------------------------
  // Injects
  // -----------------------------
  private location = inject(Location);
  private locationStrategy = inject(LocationStrategy);
  private iconService = inject(IconService);

  // -----------------------------
  // Outputs
  // -----------------------------
  NavCollapsedMob = output();

  // -----------------------------
  // State
  // -----------------------------
  navigations: NavigationItem[] = [];
  isMenuReady = false;

  windowWidth = window.innerWidth;
  title = 'Hebert application for quick deploy';
  currentApplicationVersion = environment.appVersion;

  // -----------------------------
  // Auth context
  // -----------------------------
  userRole = localStorage.getItem('rol') || '';
  permissionList = (localStorage.getItem('permissions') || '')
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);

  permissionSet = new Set<string>(this.permissionList);
// -----------------------------
// ==============================
// MENU RESTRICTIONS BY ROLE
// ==============================
SCHOOL_MENU_RESTRICTIONS: Record<string, string[]> = {
  Ventas: [
    'calendary',
    'gruposlist',
    'listalumno',
    'teacher',
    'penditepago'
  ],

  // Future examples 👇
  // Teacher: ['aperturas', 'newalumno'],
  // Texp: ['aperturas'],
  // Assmt: ['aperturas'],
};
  // -----------------------------
  // Role helpers (READABLE)
  // -----------------------------
  isSysAdmin = this.userRole === 'SysAdmin';
  isDirAcademico = this.userRole === 'diraca';
  isRH = this.userRole === 'RH';
  isVentas = this.userRole === 'Ventas';
  isAdmin = this.userRole === 'Administrador';
  isTeacherLike = ['Teacher', 'Texp', 'Assmt'].includes(this.userRole);

  constructor() {
    // Register icons ONCE
    this.iconService.addIcon(
      DashboardOutline,
      CreditCardOutline,
      FontSizeOutline,
      LoginOutline,
      ProfileOutline,
      BgColorsOutline,
      AntDesignOutline,
      ChromeOutline,
      QuestionOutline
    );

    // 🔁 IMPORTANT:
    // Clone NavigationItems so we NEVER mutate the source
    let menu: NavigationItem[] = JSON.parse(JSON.stringify(NavigationItems));

    // ---------------------------------
    // 1️⃣ BASE ROLE FILTERING
    // ---------------------------------

    // Non admins → hide settings
    if (!this.isSysAdmin && !this.isDirAcademico && !this.isAdmin && !this.isRH) {
      menu = menu.filter(item => item.id !== 'settings');
    }

    // Teacher-like → hide dashboard
    if (this.isTeacherLike) {
      menu = menu.filter(item => item.id !== 'dashboard');
    }

    // ---------------------------------
    // 2️⃣ SCHOOL MENU CHILD FILTERING
    // ---------------------------------
    menu = menu.map(item => {
      if (item.id !== 'school' || !item.children) return item;

      let children = [...item.children];

      // Teacher & DirAcad restrictions
      if (this.isTeacherLike || this.isDirAcademico) {
        const forbidden = ['newalumno', 'teacher', 'penditepago'];
        children = children.filter(c => !forbidden.includes(c.id));
      }

      // Teachers only
      if (this.isTeacherLike) {
        children = children.filter(c => c.id !== 'aperturas');
      }

      // Add "Mis Demos" if applicable
      if ((this.isTeacherLike || this.isDirAcademico)
          && !children.some(c => c.id === 'mis_demos')) {

        children.push({
          id: 'mis_demos',
          title: 'Mis Demos',
          type: 'item',
          classes: 'nav-item',
          url: '/Mis_Demos',
          icon: 'how_to_vote',
          breadcrumbs: false
        });
      }

      return { ...item, children };
    });
menu = menu.map(item => {
  if (item.id !== 'school' || !item.children) {
    return item;
  }

  const filteredChildren = this.filterChildrenByRole(
    item.children,
    this.userRole
  );

  return {
    ...item,
    children: filteredChildren
  };
});

    // ---------------------------------
    // 3️⃣ TEACHER DASHBOARD OVERRIDE
    // ---------------------------------
    if (this.isTeacherLike) {
      menu.unshift({
        id: 'dashboard',
        title: 'Dashboard',
        type: 'group',
        icon: 'dashboard',
        children: [
          {
            id: 'teacher_dashboard',
            title: 'Dashboard',
            type: 'item',
            classes: 'nav-item',
            url: '/dashboard/teacher',
            icon: 'analytics',
            breadcrumbs: false
          }
        ]
      });
    }

    // ---------------------------------
    // 4️⃣ MANAGEMENT GROUP (PERMISSIONS)
    // ---------------------------------
    const managementChildren: NavigationItem[] = [];

    if (this.permissionSet.has('3')) {
      managementChildren.push({
        id: 'perfil',
        title: 'Empleados',
        type: 'item',
        classes: 'nav-item',
        url: '/HR',
        icon: 'manage_accounts',
        breadcrumbs: false
      });
    }

    if (this.permissionSet.has('8')) {
      managementChildren.push({
        id: 'Alumnos',
        title: 'Alumnos',
        type: 'item',
        classes: 'nav-item',
        url: '/Finazas_Alumnos',
        icon: 'payments',
        breadcrumbs: false
      });
    }

    if (this.permissionSet.has('11')) {
      managementChildren.push({
        id: 'EmpleadosF',
        title: 'Finanzas',
        type: 'item',
        classes: 'nav-item',
        url: '/Finazas_Empleados',
        icon: 'monetization_on',
        breadcrumbs: false
      });
    }
      // ------------------------------
    //  ADDING CajaChica
    //-------------------------------
      if (this.permissionSet.has('15')) {
      // const management = menu.find(m => m.id === 'Management');
      // if (management?.children) {
      //   if (!management.children.some(c => c.id === 'Caja')) {
      //     management.children.push({
      //       id: 'Caja',
      //       title: 'Caja Recep.',
      //       type: 'item',
      //       classes: 'nav-item',
      //       url: '/caja_chica',
      //       icon: 'savings',
      //       breadcrumbs: false
      //     });
      //   }
      // }
    managementChildren.push({
            id: 'Caja',
            title: 'Caja Recep.',
            type: 'item',
            classes: 'nav-item',
            url: '/caja_chica',
            icon: 'savings',
            breadcrumbs: false
          });
    }
         // ------------------------------
    //  ADDING CajaGrande
    //-------------------------------
      if (this.permissionSet.has('16')) {
      // const management = menu.find(m => m.id === 'Management');
      // if (management?.children) {
      //   if (!management.children.some(c => c.id === 'CajaGrande')) {
      //     management.children.push({
      //       id: 'CajaGrande',
      //       title: 'Caja Grande',
      //       type: 'item',
      //       classes: 'nav-item',
      //       url: '/caja_grande',
      //       icon: 'point_of_sale',
      //       breadcrumbs: false
      //     });
      //   }
      // }
          managementChildren.push({
             id: 'CajaGrande',
            title: 'Caja Grande',
            type: 'item',
            classes: 'nav-item',
            url: '/caja_grande',
            icon: 'point_of_sale',
            breadcrumbs: false
          });
    }
        if (this.permissionSet.has('17')) {

    managementChildren.push({
            id: 'AdminRep',
            title: 'Reportes.',
            type: 'item',
            classes: 'nav-item',
            url: '/admin_report',
            icon: 'add_chart',
            breadcrumbs: false
          });
    }
    // Time off approvals (ANY of these permissions)
    const vacationApprovals = ['5', '6', '7'];
    if (vacationApprovals.some(p => this.permissionSet.has(p))) {
      managementChildren.push({
        id: 'vacaciones',
        title: 'Time Off',
        type: 'item',
        classes: 'nav-item',
        url: '/time_off',
        icon: 'beach_access',
        breadcrumbs: false
      });
    }

    if (managementChildren.length) {
      menu.push({
        id: 'Management',
        title: 'Management',
        type: 'group',
        icon: 'sensor_occupied',
        children: managementChildren
      });
    }

    // ---------------------------------
    // 5️⃣ ADMIN DEMOS (PERMISSION 9)
    // ---------------------------------
    if (this.permissionSet.has('9')) {
      const school = menu.find(m => m.id === 'school');
      if (school?.children) {
        if (!school.children.some(c => c.id === 'AdminDemo')) {
          school.children.push({
            id: 'AdminDemo',
            title: 'Admin Demos',
            type: 'item',
            classes: 'nav-item',
            url: '/admin_demos',
            icon: 'how_to_vote',
            breadcrumbs: false
          });
        }

        if (!school.children.some(c => c.id === 'Prospects')) {
          school.children.push({
            id: 'Prospects',
            title: 'Prospects',
            type: 'item',
            classes: 'nav-item',
            url: '/prospects',
            icon: 'groups_3',
            breadcrumbs: false
          });
        }
      }
    }
    // ---------------------------------
    // 5️⃣ ADMIN DEMOS SOLO PROSPECTOS (PERMISSION 12)
    // ---------------------------------
    if (this.permissionSet.has('12')) {
      const school = menu.find(m => m.id === 'school');
      if (school?.children) {
        if (!school.children.some(c => c.id === 'Prospects')) {
          school.children.push({
            id: 'Prospects',
            title: 'Prospects',
            type: 'item',
            classes: 'nav-item',
            url: '/prospects',
            icon: 'groups_3',
            breadcrumbs: false
          });
        }
      }
    }
    // ------------------------------
    //  ADDING MIS VENTAS
    //-------------------------------
      if (this.permissionSet.has('13')) {
      const school = menu.find(m => m.id === 'school');
      if (school?.children) {
        if (!school.children.some(c => c.id === 'MisVentas')) {
          school.children.push({
            id: 'MisVentas',
            title: 'Mis Ventas',
            type: 'item',
            classes: 'nav-item',
            url: '/demo_summary',
            icon: 'point_of_sale',
            breadcrumbs: false
          });
        }
      }
    }


    // ---------------------------------
    // 6️⃣ FINAL ORDERING
    // ---------------------------------
    this.navigations = this.reorderMenu(menu, 'settings', 'Management');
  }

  // -----------------------------
  // Lifecycle
  // -----------------------------
  ngOnInit(): void {
    setTimeout(() => {
      this.isMenuReady = true;
    }, 800);

    if (this.windowWidth < 1025) {
      document
        .querySelector('.coded-navbar')
        ?.classList.add('menupos-static');
    }
  }

  // -----------------------------
  // Utilities
  // -----------------------------
  reorderMenu(menu: NavigationItem[], first: string, second: string) {
    const idxA = menu.findIndex(i => i.id === first);
    const idxB = menu.findIndex(i => i.id === second);

    if (idxA !== -1 && idxB !== -1 && idxB > idxA) {
      const [item] = menu.splice(idxB, 1);
      menu.splice(idxA, 0, item);
    }
    return menu;
  }

  fireOutClick() {
    let currentUrl = this.location.path();
    const baseHref = this.locationStrategy.getBaseHref();
    if (baseHref) currentUrl = baseHref + currentUrl;

    const link = document.querySelector(`a.nav-link[href='${currentUrl}']`);
    let el = link?.parentElement;

    while (el) {
      if (el.classList.contains('coded-hasmenu')) {
        el.classList.add('coded-trigger', 'active');
        break;
      }
      el = el.parentElement;
    }
  }

  navMob() {
    const nav = document.querySelector('app-navigation.coded-navbar');
    if (this.windowWidth < 1025 && nav?.classList.contains('mob-open')) {
      this.NavCollapsedMob.emit();
    }
  }

  // Restirtion per menu
  private filterChildrenByRole(
  children: NavigationItem[] = [],
  role: string
): NavigationItem[] {

  const restrictedIds = this.SCHOOL_MENU_RESTRICTIONS[role];
  if (!restrictedIds || restrictedIds.length === 0) {
    return children;
  }

  return children.filter(child => !restrictedIds.includes(child.id));
}

}
